import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Loader2, CheckCircle2, Unplug, ChevronDown, Smartphone, PhoneCall } from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime, formatApiErrorDetail } from "@/lib/format";
import { loadFacebookSdk, listenSignup } from "@/lib/metaSdk";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const MODE = { coexistence: "Stesso numero dell'app WhatsApp Business", cloud_api: "Numero dedicato al gestionale", manual: "Collegamento manuale" };
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

const Manual = ({ onSaved }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ phone_number_id: "", waba_id: "", token: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { onSaved((await api.put("/whatsapp/account", f)).data); toast.success("WhatsApp collegato"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div>
      <button type="button" onClick={() => setOpen((v) => !v)} className="text-xs text-muted-foreground flex items-center gap-1" data-testid="wa-manual-toggle">
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} /> Collegamento manuale (per tecnici)
      </button>
      {open && (
        <div className="grid sm:grid-cols-3 gap-2 mt-3">
          <div className="space-y-1"><Label className="text-xs">Phone Number ID</Label><Input value={f.phone_number_id} onChange={(e) => setF({ ...f, phone_number_id: e.target.value })} data-testid="wa-manual-phone-id" /></div>
          <div className="space-y-1"><Label className="text-xs">WABA ID</Label><Input value={f.waba_id} onChange={(e) => setF({ ...f, waba_id: e.target.value })} data-testid="wa-manual-waba-id" /></div>
          <div className="space-y-1"><Label className="text-xs">Token di accesso</Label><Input type="password" value={f.token} onChange={(e) => setF({ ...f, token: e.target.value })} data-testid="wa-manual-token" /></div>
          <div className="sm:col-span-3 flex justify-end"><Button size="sm" variant="outline" onClick={save} disabled={busy || !f.phone_number_id || !f.token} data-testid="wa-manual-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Collega"}</Button></div>
        </div>
      )}
    </div>
  );
};

export const WhatsAppConnect = ({ onChange }) => {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const session = useRef(null);
  const update = (account) => { setCfg((c) => ({ ...c, account })); onChange?.(account); };

  useEffect(() => { api.get("/whatsapp/config").then((r) => setCfg(r.data)).catch(() => setCfg({ signup_available: false, account: { connected: false } })); }, []);
  useEffect(() => {
    if (!cfg?.signup_available || cfg.account?.connected) return undefined;
    loadFacebookSdk(cfg.app_id, cfg.graph_version).catch(() => toast.error("Impossibile caricare Facebook. Disattiva eventuali blocchi pubblicità e riprova."));
    return listenSignup((d) => {
      if (d.event === "CANCEL") { toast.message("Collegamento annullato"); setBusy(false); }
      else if (d.event === "ERROR") { toast.error(d.data?.error_message || "Errore durante il collegamento"); setBusy(false); }
      else session.current = d;
    });
  }, [cfg?.signup_available, cfg?.account?.connected, cfg?.app_id, cfg?.graph_version]);

  const finish = async (code) => {
    for (let i = 0; i < 12 && !session.current; i += 1) await wait(250);
    const s = session.current || {};
    try {
      const r = await api.post("/whatsapp/connect", { code, event: s.event || "", waba_id: s.data?.waba_id || "", phone_number_id: s.data?.phone_number_id || "" });
      update(r.data); toast.success("WhatsApp collegato!");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const start = (coexist) => {
    if (!window.FB) { toast.error("Facebook non è ancora pronto, riprova tra un secondo."); return; }
    session.current = null; setBusy(true);
    window.FB.login((resp) => {
      const code = resp?.authResponse?.code;
      if (code) finish(code); else setBusy(false);
    }, { config_id: cfg.config_id, response_type: "code", override_default_response_type: true,
      extras: { setup: {}, ...(coexist ? { featureType: "whatsapp_business_app_onboarding" } : {}) } });
  };
  const disconnect = async () => { await api.delete("/whatsapp/account"); update({ connected: false }); toast.success("WhatsApp scollegato"); };

  if (!cfg) return null;
  const acc = cfg.account || {};
  return (
    <div className="rounded-xl border border-border bg-card p-6 space-y-5" data-testid="wa-connect">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 flex items-center justify-center"><MessageCircle className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-semibold">WhatsApp nel gestionale</h3>
          <p className="text-xs text-muted-foreground">Leggi e rispondi ai clienti da qui, con il servizio ufficiale WhatsApp di Meta.</p>
        </div>
        {acc.connected && <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" data-testid="wa-connected-badge">Collegato</span>}
      </div>
      {acc.connected ? (
        <div className="space-y-3">
          <div className="rounded-lg bg-secondary/50 p-4 text-sm space-y-1" data-testid="wa-account-info">
            <div className="font-semibold text-base" data-no-translate>{acc.display_phone || acc.phone_number_id} {acc.verified_name ? `· ${acc.verified_name}` : ""}</div>
            <div className="text-muted-foreground">{MODE[acc.mode] || ""}</div>
            <div className="flex items-center gap-1.5 text-muted-foreground">
              {acc.last_webhook_at ? <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Messaggi in arrivo attivi (ultimo: {fmtDateTime(acc.last_webhook_at)})</> : "In attesa del primo messaggio di un cliente…"}
            </div>
          </div>
          <div className="flex justify-end"><Button variant="ghost" className="text-destructive" onClick={disconnect} data-testid="wa-disconnect-button"><Unplug className="w-4 h-4 mr-1.5" /> Scollega</Button></div>
        </div>
      ) : cfg.signup_available ? (
        <div className="space-y-4">
          <ol className="text-sm space-y-1.5 list-decimal pl-5 text-muted-foreground">
            <li>Premi il pulsante e accedi con il tuo account Facebook.</li>
            <li>Scegli (o crea) l'account WhatsApp Business della ditta.</li>
            <li>Conferma il numero: puoi usare quello che hai già sull'app WhatsApp Business.</li>
          </ol>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={() => start(true)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wa-connect-existing-button">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Smartphone className="w-4 h-4 mr-1.5" />} Collega il mio WhatsApp Business
            </Button>
            <Button variant="outline" onClick={() => start(false)} disabled={busy} data-testid="wa-connect-new-button"><PhoneCall className="w-4 h-4 mr-1.5" /> Usa un numero nuovo</Button>
          </div>
          <p className="text-xs text-muted-foreground">Le risposte gratuite sono possibili entro 24 ore dall'ultimo messaggio del cliente (regola di WhatsApp).</p>
        </div>
      ) : (
        <div className="rounded-xl bg-sky-50 dark:bg-sky-950/30 text-sky-900 dark:text-sky-200 p-4 text-sm space-y-1" data-testid="wa-not-available">
          <div className="font-semibold text-base">In arrivo</div>
          <div>Presto potrai leggere e rispondere ai clienti su WhatsApp direttamente da qui. Intanto puoi già mandare preventivi e fatture con il pulsante «Invia su WhatsApp»: il PDF si allega da solo.</div>
          {user?.is_superadmin && <div className="pt-1">Super Admin: configura l'app Meta in <Link to="/aziende" className="underline font-medium">Aziende → WhatsApp</Link>.</div>}
        </div>
      )}
      {!acc.connected && user?.is_superadmin && <Manual onSaved={update} />}
    </div>
  );
};

export default WhatsAppConnect;
